import { useState, useEffect } from "react";
import { api, errText } from "../../lib/api";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Skeleton } from "../../components/ui/skeleton";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../../components/ui/select";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Switch } from "../../components/ui/switch";
import { Megaphone, AlertTriangle, GripVertical, ChevronUp, ChevronDown, Trophy, MousePointerClick, Trash2, Plus, Loader2, Check, BarChart3 } from "lucide-react";

const TICKER_STYLE_OPTIONS = [
  { value: "plain", label: "Plain text" },
  { value: "orange", label: "Orange highlight bar" },
  { value: "navy", label: "Navy highlight bar" },
];
const TICKER_SPEED_OPTIONS = [
  { value: "slow", label: "Slow" },
  { value: "normal", label: "Normal" },
  { value: "fast", label: "Fast" },
];
const TICKER_SPEED_MS = { slow: "45s", normal: "30s", fast: "16s" };
const TICKER_PREVIEW_CFG = {
  plain: { wrap: "bg-slate-50 border border-slate-100", text: "text-[#12234A]", accent: "text-[#F5551E]" },
  orange: { wrap: "bg-[#F5551E]", text: "text-white", accent: "text-white/70" },
  navy: { wrap: "bg-[#12234A]", text: "text-white", accent: "text-[#F5551E]" },
};
const newKey = () => (typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : String(Math.random()));
const isoToLocal = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};
const localToIso = (v) => (v ? new Date(v).toISOString() : "");

const msgShowing = (m, now) => {
  const s = m.start_at ? new Date(m.start_at) : null;
  const e = m.end_at ? new Date(m.end_at) : null;
  if (s && now < s) return false;
  if (e && now > e) return false;
  return true;
};

const mapMsg = (m) => ({ _key: m.id || newKey(), id: m.id || "", text: m.text || "", link: m.link || "", start_at: isoToLocal(m.start_at), end_at: isoToLocal(m.end_at), clicks: m.clicks || 0 });
const emptyMsg = () => ({ _key: newKey(), id: "", text: "", link: "", start_at: "", end_at: "", clicks: 0 });

export const TickerSettings = () => {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [stats, setStats] = useState(null);
  const [dragIdx, setDragIdx] = useState(null);
  const loadStats = () => api.get("/admin/ticker/click-stats?days=14").then((r) => setStats(r.data)).catch(() => {});
  useEffect(() => {
    api.get("/admin/ticker").then((r) => setForm({
      enabled: r.data.enabled,
      style: r.data.style || "plain",
      speed: r.data.speed || "normal",
      start_at: isoToLocal(r.data.start_at),
      end_at: isoToLocal(r.data.end_at),
      messages: (r.data.messages || []).length ? r.data.messages.map(mapMsg) : [emptyMsg()],
    })).catch((e) => toast.error(errText(e)));
  }, []);
  const setMsg = (i, field, val) => setForm((f) => ({ ...f, messages: f.messages.map((m, j) => j === i ? { ...m, [field]: val } : m) }));
  const addMsg = () => setForm((f) => ({ ...f, messages: [...f.messages, emptyMsg()] }));
  const removeMsg = (i) => setForm((f) => ({ ...f, messages: f.messages.filter((_, j) => j !== i) }));
  const moveMsg = (from, to) => setForm((f) => {
    if (to < 0 || to >= f.messages.length) return f;
    const arr = [...f.messages];
    const [it] = arr.splice(from, 1);
    arr.splice(to, 0, it);
    return { ...f, messages: arr };
  });
  useEffect(() => { loadStats(); }, []);
  const save = async () => {
    setSaving(true);
    try {
      const messages = form.messages
        .map((m) => ({ id: m.id || "", text: (m.text || "").trim(), link: (m.link || "").trim(), start_at: localToIso(m.start_at), end_at: localToIso(m.end_at) }))
        .filter((m) => m.text);
      const { data } = await api.put("/admin/ticker", {
        enabled: form.enabled, style: form.style, speed: form.speed, messages,
        start_at: localToIso(form.start_at), end_at: localToIso(form.end_at),
      });
      setForm((f) => ({ ...f, messages: (data.messages || []).length ? data.messages.map(mapMsg) : [emptyMsg()] }));
      toast.success("Header ticker saved");
      loadStats();
    } catch (e) { toast.error(errText(e)); }
    finally { setSaving(false); }
  };
  if (!form) return <Skeleton className="h-40 rounded-xl" />;
  const now = new Date();
  const globalOk = (!form.start_at || now >= new Date(form.start_at)) && (!form.end_at || now <= new Date(form.end_at));
  const liveMsgs = form.messages.filter((m) => (m.text || "").trim() && msgShowing(m, now));
  const hasText = form.messages.some((m) => (m.text || "").trim());
  const liveNow = form.enabled && globalOk && liveMsgs.length > 0;
  const pcfg = TICKER_PREVIEW_CFG[form.style] || TICKER_PREVIEW_CFG.plain;
  const maxDay = stats ? Math.max(1, ...stats.days.map((d) => d.count)) : 1;
  const maxMsg = stats ? Math.max(1, ...stats.messages.map((m) => m.clicks)) : 1;
  const topId = stats && stats.messages.some((m) => m.clicks > 0)
    ? stats.messages.reduce((a, b) => (b.clicks > a.clicks ? b : a)).id : null;
  const statMap = {};
  if (stats) stats.messages.forEach((m) => { statMap[m.id] = m; });
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-5 max-w-2xl" data-testid="ticker-settings">
      <h3 className="font-display font-bold text-[#12234A] flex items-center gap-2 mb-1"><Megaphone size={18} className="text-[#F5551E]" /> Header ticker</h3>
      <p className="text-[11px] text-slate-400 mb-4">Scrolling announcements shown in the header between the logo and the buttons (desktop &amp; tablet). Add several and they rotate through the loop.</p>

      <div className={`flex items-center gap-2 rounded-xl px-3 py-2 mb-4 text-xs font-semibold ${liveNow ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`} data-testid="ticker-status">
        <span className={`w-2 h-2 rounded-full ${liveNow ? "bg-emerald-500" : "bg-slate-400"}`} />
        {liveNow ? "Live now — visitors can see the ticker" : "Hidden right now"}
      </div>
      {form.enabled && hasText && !liveNow && (
        <div className="flex items-start gap-2 rounded-xl bg-amber-50 border border-amber-200 px-3 py-2 mb-4" data-testid="ticker-autopause-notice">
          <AlertTriangle size={14} className="text-amber-600 mt-0.5 shrink-0" />
          <p className="text-[11px] text-amber-800">The ticker is switched on but nothing is showing — {globalOk ? "every message is outside its own date window (expired or not started yet)." : "the global date window is not active right now."} It will auto-appear when a message becomes active.</p>
        </div>
      )}

      <div className="flex items-center justify-between rounded-xl border border-slate-100 px-3 py-2 mb-4">
        <div>
          <p className="text-sm font-semibold text-[#12234A]">Show ticker</p>
          <p className="text-[11px] text-slate-400">Master switch. Scheduling below can auto-hide it too.</p>
        </div>
        <Switch checked={form.enabled} onCheckedChange={(v) => setForm((f) => ({ ...f, enabled: v }))} data-testid="ticker-enabled-switch" />
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
        <div>
          <Label className="text-xs font-semibold text-slate-500">Style</Label>
          <Select value={form.style} onValueChange={(v) => setForm((f) => ({ ...f, style: v }))}>
            <SelectTrigger className="mt-1.5 rounded-xl" data-testid="ticker-style-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {TICKER_STYLE_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value} data-testid={`ticker-style-${o.value}`}>{o.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs font-semibold text-slate-500">Speed</Label>
          <Select value={form.speed} onValueChange={(v) => setForm((f) => ({ ...f, speed: v }))}>
            <SelectTrigger className="mt-1.5 rounded-xl" data-testid="ticker-speed-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {TICKER_SPEED_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value} data-testid={`ticker-speed-${o.value}`}>{o.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs font-semibold text-slate-500">Start (optional)</Label>
          <Input type="datetime-local" value={form.start_at} onChange={(e) => setForm((f) => ({ ...f, start_at: e.target.value }))} data-testid="ticker-start-input" className="mt-1.5 rounded-xl" />
        </div>
        <div>
          <Label className="text-xs font-semibold text-slate-500">End (optional)</Label>
          <Input type="datetime-local" value={form.end_at} onChange={(e) => setForm((f) => ({ ...f, end_at: e.target.value }))} data-testid="ticker-end-input" className="mt-1.5 rounded-xl" />
        </div>
      </div>
      <p className="text-[10px] text-slate-400 -mt-2 mb-4">Global window: leave blank to run indefinitely. The ticker shows only when enabled <em>and</em> within this window; each message can also have its own window below.</p>

      <Label className="text-xs font-semibold text-slate-500">Messages</Label>
      <div className="space-y-3 mt-1.5" data-testid="ticker-messages">
        {form.messages.map((m, i) => (
          <div
            key={m._key}
            className={`rounded-xl border p-3 transition-colors ${dragIdx === i ? "border-[#F5551E] bg-[#FFF9F6]" : "border-slate-100"}`}
            data-testid={`ticker-message-${i}`}
            draggable
            onDragStart={() => setDragIdx(i)}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => { e.preventDefault(); if (dragIdx !== null && dragIdx !== i) moveMsg(dragIdx, i); setDragIdx(null); }}
            onDragEnd={() => setDragIdx(null)}
          >
            <div className="flex items-start gap-2">
              <div className="flex flex-col items-center gap-0.5 mt-1 shrink-0">
                <span className="cursor-grab active:cursor-grabbing text-slate-300 hover:text-slate-500" data-testid={`ticker-drag-${i}`} title="Drag to reorder"><GripVertical size={16} /></span>
                <span className="text-[11px] font-bold text-slate-400">{i + 1}</span>
                <button onClick={() => moveMsg(i, i - 1)} disabled={i === 0} data-testid={`ticker-up-${i}`} className="text-slate-400 hover:text-[#F5551E] disabled:opacity-25" title="Move up"><ChevronUp size={15} /></button>
                <button onClick={() => moveMsg(i, i + 1)} disabled={i === form.messages.length - 1} data-testid={`ticker-down-${i}`} className="text-slate-400 hover:text-[#F5551E] disabled:opacity-25" title="Move down"><ChevronDown size={15} /></button>
              </div>
              <div className="flex-1 min-w-0">
                <Textarea value={m.text} onChange={(e) => setMsg(i, "text", e.target.value)} data-testid={`ticker-text-${i}`} rows={2} maxLength={200} placeholder="e.g. Spring adoption event this weekend — half-price featured listings!" className="rounded-xl" />
                <div className="flex items-center gap-2 mt-1.5">
                  <Input value={m.link} onChange={(e) => setMsg(i, "link", e.target.value)} data-testid={`ticker-link-${i}`} placeholder="Optional link: https://… or /browse" className="rounded-xl h-9 text-sm" />
                  <span className="text-[10px] text-slate-400 shrink-0">{(m.text || "").length}/200</span>
                </div>
                <div className="grid grid-cols-2 gap-2 mt-2">
                  <div>
                    <Label className="text-[10px] font-semibold text-slate-400">Show from</Label>
                    <Input type="datetime-local" value={m.start_at} onChange={(e) => setMsg(i, "start_at", e.target.value)} data-testid={`ticker-msg-start-${i}`} className="rounded-xl h-9 text-sm mt-0.5" />
                  </div>
                  <div>
                    <Label className="text-[10px] font-semibold text-slate-400">Show until</Label>
                    <Input type="datetime-local" value={m.end_at} onChange={(e) => setMsg(i, "end_at", e.target.value)} data-testid={`ticker-msg-end-${i}`} className="rounded-xl h-9 text-sm mt-0.5" />
                  </div>
                </div>
                <div className="flex items-center gap-1.5 mt-2 text-[11px] text-slate-500 flex-wrap" data-testid={`ticker-msg-clicks-${i}`}>
                  {m.id && topId === m.id && <span className="inline-flex items-center gap-1 bg-amber-100 text-amber-700 font-bold rounded-full px-2 py-0.5" data-testid={`ticker-row-top-${i}`}><Trophy size={11} /> Top</span>}
                  <MousePointerClick size={12} className="text-[#F5551E]" />
                  <span className="font-semibold text-[#12234A]">{(statMap[m.id]?.clicks ?? m.clicks) || 0}</span> click{((statMap[m.id]?.clicks ?? m.clicks) || 0) === 1 ? "" : "s"}
                  {m.id && statMap[m.id] && <span className="text-slate-400">· {statMap[m.id].impressions.toLocaleString()} views · <span className="font-semibold text-[#12234A]">{statMap[m.id].ctr}% CTR</span></span>}
                  {!m.link && <span className="text-slate-300">· add a link to track clicks</span>}
                </div>
              </div>
              <button onClick={() => removeMsg(i)} disabled={form.messages.length <= 1} data-testid={`ticker-remove-${i}`} className="text-red-500 hover:text-red-600 disabled:opacity-30 mt-2 shrink-0" title="Remove message"><Trash2 size={15} /></button>
            </div>
          </div>
        ))}
      </div>
      <Button variant="outline" onClick={addMsg} disabled={form.messages.length >= 10} data-testid="ticker-add-message" className="mt-3 rounded-xl gap-1.5"><Plus size={15} /> Add message</Button>

      {form.enabled && liveMsgs.length > 0 && (
        <div className="mt-5">
          <p className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold mb-1.5">Live preview</p>
          <div className={`ticker-mask overflow-hidden rounded-full py-1.5 ${pcfg.wrap} ${pcfg.text}`} data-testid="ticker-preview">
            <div className="ticker-track" style={{ animationDuration: TICKER_SPEED_MS[form.speed] || TICKER_SPEED_MS.normal }}>
              {[0, 1].map((g) => (
                <div key={g} className="inline-flex items-center shrink-0">
                  {liveMsgs.map((m) => (
                    <span key={m._key} className="inline-flex items-center">
                      <span className="text-sm font-semibold px-6 whitespace-nowrap">{m.text}</span>
                      <span className={pcfg.accent}>✦</span>
                    </span>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      <Button onClick={save} disabled={saving} data-testid="ticker-save-btn" className="mt-5 rounded-xl bg-[#F5551E] hover:bg-[#E04812] text-white font-semibold gap-2">
        {saving ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />} Save ticker
      </Button>

      {stats && (
        <div className="mt-6 pt-5 border-t border-slate-100" data-testid="ticker-click-dashboard">
          <h4 className="font-display font-bold text-[#12234A] flex items-center gap-2 mb-1"><BarChart3 size={16} className="text-[#F5551E]" /> Click dashboard</h4>
          <p className="text-[11px] text-slate-400 mb-3">Link clicks over the last 14 days — <span className="font-semibold text-[#12234A]">{stats.total}</span> total.</p>
          <div className="flex items-end gap-1 h-24" data-testid="ticker-click-chart">
            {stats.days.map((d) => (
              <div key={d.date} className="flex-1 flex flex-col items-center justify-end group relative" title={`${d.date}: ${d.count}`}>
                <span className="text-[9px] text-slate-400 mb-0.5">{d.count > 0 ? d.count : ""}</span>
                <div className="w-full rounded-t bg-[#F5551E]/80 group-hover:bg-[#F5551E] transition-colors" style={{ height: `${Math.max(2, (d.count / maxDay) * 72)}px` }} />
                <span className="text-[8px] text-slate-400 mt-1">{d.date.slice(5)}</span>
              </div>
            ))}
          </div>
          {stats.messages.length > 0 && (
            <div className="mt-5">
              <p className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold mb-2">Performance by message</p>
              <div className="space-y-2.5" data-testid="ticker-click-by-message">
                {stats.messages.map((m) => (
                  <div key={m.id} className="flex items-center gap-3" data-testid={`ticker-perf-${m.id}`}>
                    <span className="text-xs text-slate-600 w-44 truncate shrink-0 flex items-center gap-1" title={m.text}>
                      {topId === m.id && <span data-testid="ticker-top-badge" title="Top performer — most clicks"><Trophy size={13} className="text-amber-500 shrink-0" /></span>}
                      {m.text}
                    </span>
                    <div className="flex-1 h-3.5 rounded-full bg-slate-100 overflow-hidden"><div className={`h-full rounded-full ${topId === m.id ? "bg-amber-500" : "bg-[#F5551E]"}`} style={{ width: `${(m.clicks / maxMsg) * 100}%` }} /></div>
                    <span className="text-[11px] text-slate-400 w-32 text-right shrink-0">{m.impressions.toLocaleString()} views · <span className="font-semibold text-[#12234A]">{m.ctr}% CTR</span></span>
                    <span className="text-xs font-bold text-[#12234A] w-8 text-right shrink-0">{m.clicks}</span>
                  </div>
                ))}
              </div>
              <p className="text-[10px] text-slate-400 mt-2">CTR = clicks ÷ times shown. Impressions count each page load where the message was live.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TickerSettings;
